import type { Device } from 'src/types/Device';
import { compareFWVersions } from 'src/helpers/firmware_compare_helpers';

/**
 * Type definition for input reader event codes.
 *
 * Maps IoTS input event codes to their string representations used in the UI.
 * These codes represent different types of button presses and scroll wheel rotations
 * for devices with input capabilities (buttons, remote controls, dimmers with rotary encoders).
 *
 * @property {string} S - Short press (single click)
 * @property {string} SS - Double short press (double click)
 * @property {string} SSS - Triple short press (triple click)
 * @property {string} L - Long press for buttons; scroll left/up for scroll wheels
 * @property {string} SL - Short press followed by long press
 * @property {string} LS - Long press followed by short press
 * @property {string} [R] - Scroll right/down direction (scroll wheel only)
 */
export type InputReadersEventsType = {
	S: string;
	SS: string;
	SSS: string;
	L: string;
	SL: string;
	LS: string;
	R?: string;
};

/**
 * Maps IoTS input reader event codes to UI display identifiers.
 *
 * This mapping is used to convert backend event codes into visual indicators
 * displayed in the UI (e.g., dots representing number of presses).
 *
 * Special handling:
 * - 'L' has dual meaning: long press for buttons, scroll left/up for scroll wheels
 * - 'R' represents scroll right/down and uses 'one' as a visual indicator
 *
 * @type {InputReadersEventsType}
 */
export const mappedInputReaderEvent: InputReadersEventsType = {
	S: 'one',
	SS: 'two',
	SSS: 'three',
	L: 'long',
	SL: 'short-long',
	LS: 'long-short',
	R: 'one',
};

/**
 * I4 models that support `Input.Trigger` for button-type inputs.
 * @see https://shelly-api-docs.shelly.cloud/gen2/ComponentsAndServices/Input/#inputtrigger
 */
export const INPUT_TRIGGER_DEVICE_TYPES =
	/^(SNSN-0024X|SNSN-0D24X|S3SN-0024X|S3SN-0D24X|S4SN-0A24X|S4SN-0D24X)$/;

/** Minimum firmware version that exposes `Input.Trigger` on supported I4 devices. */
export const INPUT_TRIGGER_MIN_FW = '1.3.0';

/**
 * Whether the device can remotely emit input events via `Input.Trigger`
 * (button-type inputs only; callers must also check input type).
 *
 * @param device - Device to check
 * @param settings - Optional device settings used for firmware version
 */
export function supportsRemoteInputTrigger(
	device: Device,
	settings?: { sys?: { device?: { fw_id?: string } }; DeviceInfo?: { ver?: string } },
): boolean {
	if (!INPUT_TRIGGER_DEVICE_TYPES.test(device?.type || '')) return false;

	const fw = settings?.sys?.device?.fw_id || settings?.DeviceInfo?.ver || '';
	if (!fw) return true;

	return compareFWVersions(fw, '>=', INPUT_TRIGGER_MIN_FW);
}
